import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './HistoryPage.css';

// Configure axios to send credentials (cookies) with requests
axios.defaults.withCredentials = true;

interface HistoryItem {
  id: number;
  task_id: string;
  company_name: string;
  domain?: string;
  country?: string;
  risk_level?: string;
  include_opensanctions: boolean;
  created_at: string;
  total_tokens?: number;
  estimated_cost_usd?: number;
}

interface HistoryResponse {
  screenings: HistoryItem[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface HistoryPageProps {
  onLoadScreening?: (id: number) => void;
}

const HistoryPage: React.FC<HistoryPageProps> = ({ onLoadScreening }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [historyData, setHistoryData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Filters
  const [filterCompany, setFilterCompany] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const fetchHistory = async (pageNum: number = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('page', pageNum.toString());
      params.append('per_page', '20');
      if (filterCompany) params.append('company', filterCompany);
      if (filterRisk) params.append('risk_level', filterRisk);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      const response = await axios.get<HistoryResponse>(`${API_BASE}/history?${params.toString()}`);
      setHistoryData(response.data);
      setPage(pageNum);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadItem = async (id: number) => {
    if (onLoadScreening) {
      onLoadScreening(id);
      navigate('/');
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this screening record?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/history/${id}`);
      fetchHistory(page);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete screening');
    }
  };

  const handleFilterApply = () => {
    fetchHistory(1);
  };

  const handleClearFilters = () => {
    setFilterCompany('');
    setFilterRisk('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <div className="history-page">
      <div className="history-page-header">
        <h1>Screening History</h1>
        <p className="history-page-subtitle">View and manage past compliance screenings</p>
      </div>

      {/* Filters Section */}
      <div className="history-filters-container">
        <button
          className="filters-toggle"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <span>Filters</span>
          <span className={`toggle-arrow ${filtersExpanded ? 'expanded' : ''}`}>&#9662;</span>
        </button>

        <div className={`history-filters ${filtersExpanded ? 'expanded' : ''}`}>
          <div className="filter-group">
            <label htmlFor="filterCompany">Company</label>
            <input
              type="text"
              id="filterCompany"
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              placeholder="Search by name"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filterRisk">Risk Level</label>
            <select
              id="filterRisk"
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
            >
              <option value="">All</option>
              <option value="LOW">LOW</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="HIGH">HIGH</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="filterDateFrom">From</label>
            <input
              type="date"
              id="filterDateFrom"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filterDateTo">To</label>
            <input
              type="date"
              id="filterDateTo"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <button type="button" onClick={handleFilterApply} className="filter-apply-btn">
              Apply
            </button>
            <button type="button" onClick={handleClearFilters} className="filter-clear-btn">
              Clear
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && (
        <div className="history-loading">
          <div className="loading-spinner"></div>
          <span>Loading history...</span>
        </div>
      )}

      {!loading && historyData && (
        <>
          {historyData.screenings.length === 0 ? (
            <div className="no-history">
              <p>No screening history found.</p>
              <p className="no-history-hint">Start a new screening to see it appear here.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="history-table-container">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Company</th>
                      <th>Country</th>
                      <th>Risk Level</th>
                      {isAdmin && <th>Tokens</th>}
                      <th>OpenSanctions</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.screenings.map((item) => {
                      const { date, time } = formatDate(item.created_at);
                      return (
                        <tr
                          key={item.id}
                          onClick={() => handleLoadItem(item.id)}
                          className="clickable-row"
                        >
                          <td>
                            <div className="date-cell">
                              <span className="date-primary">{date}</span>
                              <span className="date-secondary">{time}</span>
                            </div>
                          </td>
                          <td className="company-cell">{item.company_name}</td>
                          <td>{item.country || '-'}</td>
                          <td>
                            <span className={`risk-badge risk-${(item.risk_level || 'unknown').toLowerCase()}`}>
                              {item.risk_level || 'N/A'}
                            </span>
                          </td>
                          {isAdmin && (
                            <td className="tokens-cell">
                              {item.total_tokens ? (
                                <span title={item.estimated_cost_usd ? `Est. cost: $${item.estimated_cost_usd.toFixed(4)}` : ''}>
                                  {item.total_tokens.toLocaleString()}
                                </span>
                              ) : '-'}
                            </td>
                          )}
                          <td>{item.include_opensanctions ? 'Yes' : 'No'}</td>
                          <td>
                            <button
                              onClick={(e) => handleDelete(item.id, e)}
                              className="delete-button"
                              title="Delete this screening"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="history-cards">
                {historyData.screenings.map((item) => {
                  const { date, time } = formatDate(item.created_at);
                  return (
                    <div
                      key={item.id}
                      className="history-card"
                      onClick={() => handleLoadItem(item.id)}
                    >
                      <div className="history-card-header">
                        <span className="history-card-company">{item.company_name}</span>
                        <span className={`risk-badge risk-${(item.risk_level || 'unknown').toLowerCase()}`}>
                          {item.risk_level || 'N/A'}
                        </span>
                      </div>
                      <div className="history-card-details">
                        <div className="history-card-row">
                          <span className="label">Date:</span>
                          <span>{date} {time}</span>
                        </div>
                        {item.country && (
                          <div className="history-card-row">
                            <span className="label">Country:</span>
                            <span>{item.country}</span>
                          </div>
                        )}
                        <div className="history-card-row">
                          <span className="label">OpenSanctions:</span>
                          <span>{item.include_opensanctions ? 'Included' : 'Not included'}</span>
                        </div>
                        {isAdmin && item.total_tokens && (
                          <div className="history-card-row">
                            <span className="label">Tokens:</span>
                            <span>{item.total_tokens.toLocaleString()}{item.estimated_cost_usd ? ` ($${item.estimated_cost_usd.toFixed(4)})` : ''}</span>
                          </div>
                        )}
                      </div>
                      <div className="history-card-actions">
                        <button className="view-button">View Details</button>
                        <button
                          onClick={(e) => handleDelete(item.id, e)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {historyData.pages > 1 && (
                <div className="history-pagination">
                  <button
                    onClick={() => fetchHistory(page - 1)}
                    disabled={page <= 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {historyData.page} of {historyData.pages}
                    <span className="pagination-total">({historyData.total} total)</span>
                  </span>
                  <button
                    onClick={() => fetchHistory(page + 1)}
                    disabled={page >= historyData.pages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default HistoryPage;
